import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { API_BASE, toApiError, tokens } from "@/lib/api";
import { useT } from "@/lib/i18n";
import Marca from "@/components/Marca";
import { Button, Card, ErrorBanner, Input, Spinner } from "@/components/ui";

export default function Login() {
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const qc = useQueryClient();

  const login = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post(`${API_BASE}/auth/login/`, { email, password });
      return data as { access: string; refresh: string };
    },
    onSuccess: (data) => {
      // Descarta o cache do usuário anterior antes de entrar: sem isto o
      // ["me"] antigo (staleTime 5min) seria servido para a nova sessão.
      qc.clear();
      tokens.set(data);
      navigate("/");
    },
  });

  /** "Senha incorreta" para 401, mensagem real para o resto.
   *
   * Mostrar sempre "e-mail ou senha incorretos" faz o usuário tentar a
   * mesma senha certa dez vezes quando o problema é o servidor fora do ar
   * ou a API em outro endereço. */
  const erro =
    login.error && axios.isAxiosError(login.error) && login.error.response?.status === 401
      ? t("auth.wrongCredentials")
      : toApiError(login.error).message;

  return (
    <div className="flex min-h-dvh items-center justify-center bg-carbon-900 p-4">
      <Card className="w-full max-w-sm p-6">
        <div className="mb-6">
          <Marca />
          <p className="mt-2 text-xs text-steel-muted">{t("auth.panelSubtitle")}</p>
        </div>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            login.mutate();
          }}
        >
          <Input
            label={t("common.email")}
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label={t("auth.password")}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {login.isError && <ErrorBanner message={erro} />}

          <Button type="submit" className="w-full" disabled={login.isPending}>
            {login.isPending ? <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" /> : t("auth.signIn")}
          </Button>
        </form>
      </Card>
    </div>
  );
}
